import { AuthHelper } from "#core/helpers/auth.helper";
import { TimezoneHelper } from "#helpers/timezone.helper";
import * as loginActions from "#login/store/actions/login-form.action";
import { LocalStorage } from "#services/local-storage.service";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Auth, PublicService, RegistrationCheckService, UsersService } from "@carnegie-learning/data-access-angular-passport";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, delay, map, switchMap } from "rxjs/operators";
import * as registerActions from "../actions/login-register.action";

@Injectable()
export class LoginRegisterEffect {
    constructor(
        private actions$: Actions,
        private auth: Auth,
        private authHelper: AuthHelper,
        private registrationCheckService: RegistrationCheckService,
        private usersService: UsersService,
        private localStorage: LocalStorage,
        private timezoneHelper: TimezoneHelper,
        private router: Router,
        private publicService: PublicService
    ) {}

    registrationCheckAttempt$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerRegistrationCheckAttempt))
            .pipe(
                switchMap(action => {
                    return this.registrationCheckService.registrationCheck(action.payload).pipe(
                        map(data => {
                            if (data !== undefined && data.newUser === true) {
                                this.router.navigate([
                                    "login/onboard-new-user"
                                ]);
                            } else if (
                                data !== undefined &&
                                data.newUser === false
                            ) {
                                this.router.navigate([
                                    "login/onboard-existing-user"
                                ]);
                            } else {
                                // should never get to this step but if something major happens giving technical error
                                this.router.navigate(["login/register"]);
                                const tempError: any = {
                                    status: 403,
                                    error: {
                                        message:
                                            "Registration is experiencing technical difficulties, please try again a few minutes."
                                    }
                                };
                                return registerActions.registerRegistrationCheckFail(
                                    {
                                        payload: tempError
                                    }
                                );
                            }

                            return registerActions.registerRegistrationCheckSuccess(
                                {
                                    email: action.payload.email,
                                    inviteHash: action.payload.inviteHash
                                }
                            );
                        }),
                        catchError(error =>
                            of(
                                registerActions.registerRegistrationCheckFail({
                                    payload: error
                                })
                            )
                        )
                    );
                })
            )
    );

    registrationFormAttemptStudent$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerFormAttemptStudent))
            .pipe(
                switchMap(action => {
                    return this.usersService.createStudentAccount(action.payload).pipe(
                        map(userData =>
                            registerActions.registerFormSuccess({
                                payload: userData
                            })
                        ),
                        catchError(error =>
                            of(
                                registerActions.registerFormFail({
                                    payload: error
                                })
                            )
                        )
                    );
                })
            )
    );

    registrationFormAttemptTeacher$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerFormAttemptTeacher))
            .pipe(
                switchMap(action => {
                    return this.usersService.createTeacherAccount(action.payload).pipe(
                        map(userData =>
                            registerActions.registerFormSuccess({
                                payload: userData
                            })
                        ),
                        catchError(error =>
                            of(
                                registerActions.registerFormFail({
                                    payload: error
                                })
                            )
                        )
                    );
                })
            )
    );

    registrationFormSuccess$ = createEffect(() =>
        this.actions$.pipe(ofType(registerActions.registerFormSuccess)).pipe(
            map(action => {
                this.localStorage.clear();
                let date: any = this.timezoneHelper.convertServerDateToUserTZ(
                    action.payload.accessTokenExpireAt,
                    action.payload.timezone
                );
                date = date.toString();
                this.localStorage.set(
                    "authToken",
                    action.payload.accessToken,
                    date
                );
                this.localStorage.set("userProfile", action.payload);
                return registerActions.registerStep2();
            })
        )
    );

    registrationFormAttemptExistingStudent$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerFormAttemptExistingStudent))
            .pipe(
                switchMap(action => {
                    return this.auth
                        .registerStudentToCourse(action.payload)
                        .pipe(
                            map(userData =>
                                registerActions.registerFormSuccessExistingUser(
                                    {
                                        payload: action.payload
                                    }
                                )
                            ),
                            catchError(error =>
                                of(
                                    registerActions.registerFormFail({
                                        payload: error
                                    })
                                )
                            )
                        );
                })
            )
    );

    registrationFormAttemptExistingTeacher$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerFormAttemptExistingTeacher))
            .pipe(
                switchMap(action => {
                    return this.auth
                        .registerTeacherToSchool(action.payload)
                        .pipe(
                            map(userData =>
                                registerActions.registerFormSuccessExistingUser(
                                    {
                                        payload: action.payload
                                    }
                                )
                            ),
                            catchError(error =>
                                of(
                                    registerActions.registerFormFail({
                                        payload: error
                                    })
                                )
                            )
                        );
                })
            )
    );

    registrationFormSuccessExistingUser$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerFormSuccessExistingUser))
            .pipe(
                switchMap(action => {
                this.localStorage.clear();
                if ((<any>window).isDemo) {
                    (<any>window).location.href = ((<any>window).FIDO_PORTAL_RETURN_URL);
                }
                return of(action).pipe(delay(2000));
                }),
                map(action => {
                
                return loginActions.userLogout({signout: true});
        }))
    );

    registrationDistrictTokenCheck$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerDistrictTokenCheckAttempt))
            .pipe(
                switchMap(action => {
                    return this.publicService
                        .districtInviteTokenCheck(action.inviteHash)
                        .pipe(
                            map(data =>
                                registerActions.registerDistrictTokenCheckSuccess(
                                    {
                                        payload: data
                                    }
                                )
                            ),
                            catchError(data =>
                                of(
                                    registerActions.registerDistrictTokenCheckFail(
                                        {
                                            message: data.error.message
                                        }
                                    )
                                )
                            )
                        );
                })
            )
    );

    registrationAutoLogin$ = createEffect(() =>
        this.actions$
            .pipe(ofType(registerActions.registerAutoLogin))
            .pipe(
                map(() => {
                    const userProfile = this.localStorage.get("userProfile");
                    if (userProfile) {
                        this.authHelper.redirectUser(userProfile);
                    }
                    return { type: "[Register] Auto Login Success" };
                })
            ),
        { dispatch: false }
    );
};